import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllBank } from '../../services/BankService';
import {
    createContraVoucher,
    getContraVoucherNumber,
} from '../../services/ContraVoucherServices';
import { AccountingHintPanel, CONTRA_MODES, getBankCashOptions } from './VoucherUtils';
import { getCurrentDate } from '../../utils/DateUtils';

const CONTRA_HINT = {
    debit_ledger: 'Bank / Cash — To account (Dr)',
    credit_ledger: 'Bank / Cash — From account (Cr)',
    narration: 'Contra entry transfers funds between bank/cash accounts.',
};

function ContraVoucher() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({
        contra_date: getCurrentDate(),
        bank_date: getCurrentDate(),
        transfer_mode: 'NEFT/RTGS',
        contra_amount: '',
    });
    const [plants, setPlants] = useState([]);
    const [banks, setBanks] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [selectedFromBank, setSelectedFromBank] = useState(null);
    const [selectedToBank, setSelectedToBank] = useState(null);

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllBank(cookies).then((response) => {
            setBanks(getBankCashOptions(response.bank_list || []));
        });
    }, [cookies]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.contra_date) {
            return;
        }
        getContraVoucherNumber(
            cookies,
            inputs.plant_id,
            moment(inputs.contra_date).format('DD-MM-YYYY'),
        ).then((response) => {
            setInputs((values) => ({
                ...values,
                prefix: response.prefix,
                sl_no: response.contra_no,
                contra_no: `${response.prefix}/${response.contra_no}`,
            }));
        }).catch((error) => {
            displayError(error?.response?.data || error, 'Contra voucher number');
        });
    }, [cookies, inputs.plant_id, inputs.contra_date]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handleSelectChange = (name, option) => {
        if (name === 'from_bank_id') {
            setSelectedFromBank(option);
        }
        if (name === 'to_bank_id') {
            setSelectedToBank(option);
        }
        setInputs((values) => ({ ...values, [name]: option ? option.value : '' }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const amount = parseFloat(inputs.contra_amount) || 0;
        if (!inputs.plant_id || !inputs.from_bank_id || !inputs.to_bank_id) {
            Swal.fire('Validation', 'Plant, From Bank and To Bank are required.', 'warning');
            return;
        }
        if (inputs.from_bank_id === inputs.to_bank_id) {
            Swal.fire('Validation', 'From bank and To bank must be different.', 'warning');
            return;
        }
        if (amount <= 0) {
            Swal.fire('Validation', 'Contra amount must be greater than zero.', 'warning');
            return;
        }
        setInProgress(true);
        const payload = {
            plant_id: inputs.plant_id,
            from_bank_id: inputs.from_bank_id,
            to_bank_id: inputs.to_bank_id,
            prefix: inputs.prefix,
            sl_no: inputs.sl_no,
            contra_date: moment(inputs.contra_date).format('DD-MM-YYYY'),
            bank_date: moment(inputs.bank_date).format('DD-MM-YYYY'),
            transfer_mode: inputs.transfer_mode,
            contra_amount: amount,
            instrument_no: inputs.instrument_no || '',
            instrument_date: inputs.instrument_date
                ? moment(inputs.instrument_date).format('DD-MM-YYYY') : '',
            remarks: inputs.remarks || '',
        };
        createContraVoucher(cookies, payload)
            .then((response) => {
                Swal.fire('Saved', response.message || 'Contra voucher saved.', 'success');
                navigate(`/ContraVoucherEdit/${response.id}`);
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Contra Voucher"
                    subtitle="Transfer between bank/cash accounts — Dr To account, Cr From account."
                    modeLabel="ADD MODE"
                >
                    <form onSubmit={handleSubmit}>
                        <AccountingHintPanel hint={CONTRA_HINT} />
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={6}>
                                <label>Plant *</label>
                                <Select
                                    options={plants}
                                    value={plants.find((p) => p.value === inputs.plant_id) || null}
                                    onChange={(opt) => handleSelectChange('plant_id', opt)}
                                    placeholder="Select plant"
                                />
                            </Col>
                            <Col sm={3}>
                                <label>Contra No</label>
                                <input className="form-control" value={inputs.contra_no || ''} readOnly />
                            </Col>
                            <Col sm={3}>
                                <label>Contra Date *</label>
                                <input type="date" className="form-control" name="contra_date" value={inputs.contra_date} onChange={handleChange} />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={6}>
                                <label>From Bank / Cash (Cr) *</label>
                                <Select
                                    options={banks}
                                    value={selectedFromBank}
                                    onChange={(opt) => handleSelectChange('from_bank_id', opt)}
                                    placeholder="Select bank or cash account"
                                />
                            </Col>
                            <Col sm={6}>
                                <label>To Bank / Cash (Dr) *</label>
                                <Select
                                    options={banks}
                                    value={selectedToBank}
                                    onChange={(opt) => handleSelectChange('to_bank_id', opt)}
                                    placeholder="Select bank or cash account"
                                />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={3}>
                                <label>Transfer Mode *</label>
                                <select className="form-control" name="transfer_mode" value={inputs.transfer_mode} onChange={handleChange}>
                                    {CONTRA_MODES.map((mode) => (
                                        <option key={mode} value={mode}>{mode}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col sm={3}>
                                <label>Bank Date *</label>
                                <input type="date" className="form-control" name="bank_date" value={inputs.bank_date} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Contra Amount *</label>
                                <input type="number" step="0.01" className="form-control" name="contra_amount" value={inputs.contra_amount} onChange={handleChange} />
                            </Col>
                            <Col sm={3}>
                                <label>Instrument No</label>
                                <input className="form-control" name="instrument_no" value={inputs.instrument_no || ''} onChange={handleChange} />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={4}>
                                <label>Instrument Date</label>
                                <input type="date" className="form-control" name="instrument_date" value={inputs.instrument_date || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={8}>
                                <label>Remarks</label>
                                <input className="form-control" name="remarks" value={inputs.remarks || ''} onChange={handleChange} />
                            </Col>
                        </Row>

                        <FloatingControls>
                            <Button color="primary" type="submit" disabled={inProgress}>Save</Button>
                            <Button color="secondary" type="button" onClick={() => navigate('/ContraVoucherTable')}>Cancel</Button>
                        </FloatingControls>
                    </form>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
                <ErrorToast />
            </div>
        </div>
    );
}

export default ContraVoucher;
